/**
 * 数据校验器
 * 职责：校验埋点数据的完整性和正确性
 */

export interface TrackSchema {
  event_id: number;
  event_name: string;

  /**
   * 必填字段：必须通过 Track 组件传入
   * 如果缺失，校验会失败并阻断执行
   * ❌ 不再支持从 DOM 读取，避免维护问题
   */
  required_fields: string[];

  /**
   * 可选字段：可以通过 Track 组件传入或由上下文管理器注入
   * 如果缺失，不会校验失败，但会记录警告
   * ❌ 不再支持从 DOM 读取，避免维护问题
   */
  optional_fields: string[];

  /**
   * 字段类型定义
   */
  field_types: Record<
    string,
    "string" | "number" | "boolean" | "array" | "object"
  >;

  /**
   * 字段来源说明（用于文档和开发提示）
   * - "track": 必须通过 Track 组件传入（唯一方式）
   * - "context": 由上下文管理器自动注入（如 search_word）
   * - "auto": 自动生成（如 page_nm、timestamp 等）
   * - ❌ 不再支持 "dom"：不再从 DOM 读取，避免维护问题
   */
  field_sources?: Record<string, "track" | "context" | "auto">;

  /**
   * 自定义字段校验器
   */
  field_validators?: Record<string, (value: any) => boolean>;

  /**
   * 字段说明（用于文档和开发提示）
   */
  field_descriptions?: Record<string, string>;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

// 事件 Schema 定义
const TRACK_SCHEMAS: Record<string, TrackSchema> = {
  button_click: {
    event_id: 39008,
    event_name: "button_click",
    required_fields: ["button_name", "page_nm", "button_source"],
    optional_fields: [
      "search_word",
      "pin_id",
      "board_id",
      "module_name",
      "file_id",
    ],
    field_types: {
      button_name: "string",
      button_source: "string",
      page_nm: "string",
      search_word: "string",
      pin_id: "string",
      board_id: "string",
      module_name: "string",
      file_id: "string",
    },
    field_sources: {
      button_name: "track",
      button_source: "auto",
      page_nm: "auto",
      pin_id: "track",
      board_id: "track",
      file_id: "track",
      module_name: "track",
      search_word: "context",
    },
    field_descriptions: {
      button_name: "按钮名称，如：下载、采集、关注等，必须通过 Track 组件传入",
      button_source: "按钮来源，自动生成，格式：页面名_子页面名_模块名",
      page_nm: "页面名称，自动从 getPage() 获取",
      pin_id: "素材ID，必须通过 Track 组件传入",
      board_id: "画板ID，必须通过 Track 组件传入",
      file_id: "文件ID，必须通过 Track 组件传入",
      module_name: "模块名称，必须通过 Track 组件传入",
      search_word: "搜索词，由 SearchContext 自动注入，不需要手动传入",
    },
  },

  material_click: {
    event_id: 33002,
    event_name: "material_click",
    required_fields: ["pin_id", "page_nm", "pin_source"],
    optional_fields: ["search_word", "board_id", "content_type"],
    field_types: {
      pin_id: "string",
      pin_source: "string",
      page_nm: "string",
      search_word: "string",
      board_id: "string",
      content_type: "string",
    },
    field_sources: {
      pin_id: "track",
      pin_source: "auto",
      page_nm: "auto",
      board_id: "track",
      content_type: "track",
      search_word: "context",
    },
    field_descriptions: {
      pin_id: "素材ID，必须通过 Track 组件传入",
      pin_source: "素材来源，自动生成",
      page_nm: "页面名称，自动获取",
      search_word: "搜索词，由 SearchContext 自动注入",
    },
  },

  board_click: {
    event_id: 33008,
    event_name: "board_click",
    required_fields: ["board_id", "page_nm"],
    optional_fields: ["search_word", "pin_id", "board_source"],
    field_types: {
      board_id: "string",
      board_source: "string",
      page_nm: "string",
      search_word: "string",
      pin_id: "string",
    },
    field_sources: {
      board_id: "track",
      board_source: "auto",
      page_nm: "auto",
      pin_id: "track",
      search_word: "context",
    },
    field_descriptions: {
      board_id: "画板ID，必须通过 Track 组件传入",
      board_source: "画板来源，自动生成",
      page_nm: "页面名称，自动获取",
      search_word: "搜索词，由 SearchContext 自动注入",
    },
  },

  button_expose: {
    event_id: 39024,
    event_name: "button_expose",
    required_fields: ["button_name", "page_nm", "button_source"],
    optional_fields: [
      "search_word",
      "pin_id",
      "board_id",
      "module_name",
      "file_id",
    ],
    field_types: {
      button_name: "string",
      button_source: "string",
      page_nm: "string",
      search_word: "string",
      pin_id: "string",
      board_id: "string",
      module_name: "string",
      file_id: "string",
    },
    field_sources: {
      button_name: "track",
      button_source: "auto",
      page_nm: "auto",
      pin_id: "track",
      board_id: "track",
      file_id: "track",
      module_name: "track",
      search_word: "context",
    },
    field_descriptions: {
      button_name: "按钮名称，如：下载、采集、关注等，必须通过 Track 组件传入",
      button_source: "按钮来源，自动生成，格式：页面名_子页面名_模块名",
      page_nm: "页面名称，自动从 getPage() 获取",
      pin_id: "素材ID，必须通过 Track 组件传入",
      board_id: "画板ID，必须通过 Track 组件传入",
      file_id: "文件ID，必须通过 Track 组件传入",
      module_name: "模块名称，必须通过 Track 组件传入",
      search_word: "搜索词，由 SearchContext 自动注入，不需要手动传入",
    },
  },

  content_expose: {
    event_id: 33009,
    event_name: "content_expose",
    required_fields: ["pin_id", "page_nm", "pin_source"],
    optional_fields: ["search_word", "board_id", "expose_index"],
    field_types: {
      pin_id: "string",
      page_nm: "string",
      search_word: "string",
      board_id: "string",
      expose_index: "number",
      pin_source: "string",
    },
    field_sources: {
      pin_id: "track",
      page_nm: "auto",
      pin_source: "auto",
      expose_index: "track",
      search_word: "context",
    },
  },

  search: {
    event_id: 39005,
    event_name: "search",
    required_fields: ["search_word", "search_result_number", "page_nm"],
    optional_fields: [
      "sort_type",
      "colors",
      "filter_one",
      "filter_two",
      "search_word_type",
      "search_type",
      "original_search_word",
      "top_label_list",
      "top_label_depth",
      "filter_ids",
    ],
    field_types: {
      search_word: "string",
      search_result_number: "number",
      page_nm: "string",
      sort_type: "string",
      colors: "string",
      filter_one: "array",
      filter_two: "array",
      search_word_type: "string",
      search_type: "string",
      original_search_word: "string",
      top_label_list: "array",
      top_label_depth: "number",
      filter_ids: "array",
    },
    field_sources: {
      search_word: "track",
      search_result_number: "track",
      page_nm: "auto",
      sort_type: "track",
      colors: "track",
      filter_one: "auto", // 由 SearchEventEnricher 自动注入
      filter_two: "auto", // 由 SearchEventEnricher 自动注入
      search_word_type: "context", // 由 SearchContext 自动注入
      search_type: "context", // 由 SearchContext 自动注入
      original_search_word: "context", // 由 SearchContext 自动注入
      top_label_list: "context", // 由 SearchContext 自动注入
      top_label_depth: "context", // 由 SearchContext 自动注入
      filter_ids: "context", // 由 SearchContext 自动注入
    },
    field_descriptions: {
      search_word: "搜索词，必须通过 trackSearch 函数传入",
      search_result_number: "搜索结果数量，必须通过 trackSearch 函数传入",
      page_nm: "页面名称，自动获取",
      sort_type: "排序类型，可选",
      colors: "颜色筛选，可选",
      filter_one: "筛选器一，由 SearchEventEnricher 自动注入",
      filter_two: "筛选器二，由 SearchEventEnricher 自动注入",
      search_word_type: "搜索词类型，由 SearchContext 自动注入",
      search_type: "搜索类型，由 SearchContext 自动注入",
      original_search_word: "原始搜索词，由 SearchContext 自动注入",
      top_label_list: "顶部标签列表，由 SearchContext 自动注入",
      top_label_depth: "顶部标签深度，由 SearchContext 自动注入",
      filter_ids: "筛选器ID列表，由 SearchContext 自动注入",
    },
  },

  // ... 其他事件 Schema 可以根据需要添加
};

export class Validator {
  /**
   * 校验埋点数据
   *
   * ⚠️ 校验时机：
   * - 开发环境：校验失败会 console.error 并抛出错误，阻断执行
   * - 生产环境：校验失败记录日志并上报监控，不阻断执行（避免影响用户体验）
   *
   * ✅ 编译时校验：通过 TypeScript 类型定义，缺少必填字段会编译错误
   * ✅ 代码检查：通过 ESLint 规则，提交代码前检查
   * ✅ 运行时校验：开发环境必填字段缺失直接报错阻断，确保问题在开发阶段发现
   */
  validate(eventName: string, data: Record<string, any>): ValidationResult {
    const schema = TRACK_SCHEMAS[eventName];
    if (!schema) {
      const error = `Unknown event: ${eventName}`;
      this.handleError(error, eventName, data);
      return {
        valid: false,
        errors: [error],
        warnings: [],
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];

    // 检查必填字段
    schema.required_fields.forEach((field) => {
      if (
        !(field in data) ||
        data[field] === undefined ||
        data[field] === null ||
        data[field] === ""
      ) {
        const error = `缺少必填字段：${field}`;
        errors.push(error);
      }
    });

    // 检查字段类型
    Object.keys(data).forEach((field) => {
      const expectedType = schema.field_types[field];
      if (expectedType) {
        const value = data[field];
        // ⚠️ 重要：对于 optional_fields，如果值为 undefined，跳过类型检查
        // 因为 optional_fields 允许不存在或为 undefined
        const isOptional = schema.optional_fields?.includes(field);
        if (isOptional && value === undefined) {
          return; // 跳过类型检查
        }

        const actualType = this.getType(value);
        if (actualType !== expectedType && actualType !== "null") {
          errors.push(
            `字段 ${field} 类型错误：期望 ${expectedType}，实际 ${actualType}`
          );
        }
      }
    });

    // 执行自定义校验器
    if (schema.field_validators) {
      Object.entries(schema.field_validators).forEach(([field, validator]) => {
        if (field in data && !validator(data[field])) {
          errors.push(`Validation failed for ${field}`);
        }
      });
    }

    // 检查搜索相关页面的搜索词
    const searchPages = [
      "搜索结果页",
      "专题页",
      "主题页",
      "相似商用素材页",
      "相似素材推荐页",
    ];
    if (searchPages.includes(data.page_nm) && !data.search_word) {
      warnings.push(
        `Search-related page "${data.page_nm}" missing search_word`
      );
    }

    const result = {
      valid: errors.length === 0,
      errors,
      warnings,
    };

    // 处理校验结果
    if (!result.valid) {
      this.handleError(errors.join(", "), eventName, data);
    }

    return result;
  }

  /**
   * 错误处理
   * 开发环境：抛出错误阻止执行，确保问题在开发阶段发现
   * 生产环境：只记录日志并上报监控，不阻止执行（避免影响用户体验）
   */
  private handleError(
    error: string,
    eventName: string,
    data: Record<string, any>
  ): void {
    const errorMessage = `[TrackerV2] ${eventName} 事件校验失败：${error}`;

    if (process.env.NODE_ENV === "development") {
      // 开发环境：在控制台显示详细信息并抛出错误，阻断执行
      console.group(`❌ Tracker 校验失败：${eventName}`);
      console.error("错误信息：", error);
      console.error("缺少必填字段或字段类型错误，请检查事件 Schema：");
      console.table(data);
      console.groupEnd();

      // 开发环境直接抛出错误，阻断执行
      throw new Error(
        `[TrackerV2] ${eventName} 事件校验失败：${error}\n\n请在 Track 组件的 data props 中传入所有必填字段，或确保 Enricher 正确注入了所有字段。`
      );
    } else {
      // 生产环境：只记录日志并上报监控，不阻止执行
      console.error(errorMessage);
      // 上报到监控系统
      this.reportToMonitoring(errorMessage, eventName, data);
    }
  }

  private reportToMonitoring(
    error: string,
    eventName: string,
    data: Record<string, any>
  ): void {
    // 上报到监控系统
    // 例如：Sentry、日志系统等
    try {
      if (typeof window !== "undefined" && (window as any).Sentry) {
        (window as any).Sentry.captureException(new Error(error), {
          tags: {
            eventName,
            module: "tracker-v2",
          },
          extra: {
            data,
          },
        });
      }
    } catch (e) {
      // 降级处理
    }
  }

  private getType(value: any): string {
    if (Array.isArray(value)) return "array";
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    return typeof value;
  }

  /**
   * 注册事件 Schema
   */
  registerSchema(eventName: string, schema: TrackSchema): void {
    TRACK_SCHEMAS[eventName] = schema;

    if (process.env.NODE_ENV === "development") {
      console.log(`[Validator] Registered schema for: ${eventName}`);
    }
  }

  /**
   * 获取事件 Schema（用于开发工具和文档生成）
   */
  getSchema(eventName: string): TrackSchema | null {
    return TRACK_SCHEMAS[eventName] || null;
  }

  /**
   * 获取所有事件 Schema（用于文档生成）
   */
  getAllSchemas(): Record<string, TrackSchema> {
    return { ...TRACK_SCHEMAS };
  }
}

// 导出单例
export const validator = new Validator();
